import React from 'react';
import styled from 'styled-components';

import Button from '@components/ButtonTs';
import CopyOnClick from '@components/CRM/Common/CopyOnClick';
import CurrencyFormat from '@components/CurrencyFormat';
import OrderStatus from '@components/CRM/Orders/OrderStatus';
import { Avatar, Clock, Flight } from '@icons/common';
import { Calendar } from '@icons/userflow';

import ClientRelation from '../../clients/ClientRelation';
import HotelName from '../../components/HotelName';
import { B2BOrder } from '../types';
import OrderHistory from './OrderHistory';
import TouristsBlock from './TouristsBlock';

interface OrderDetailsProps {
  order: B2BOrder;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ order }) => {
  const paymentUrl = `${window.location.origin}/transaction?orderID=${encodeURIComponent(
    order.id,
  )}`;

  return (
    <Details>
      <DetailsHeader>
        <div>
          <Muted>
            Заказ №{order.id} · создан {order.createdAt}
          </Muted>
          <h2>{order.hotelName}</h2>
          <ClientLink>
            {order.touristName} · {order.clientPhone}
          </ClientLink>
        </div>
        <HeaderStatus status={order.status} />
      </DetailsHeader>

      <DetailGrid>
        <MainColumn>
          <Block>
            <BlockTitle>Состав тура</BlockTitle>
            <TourHero>
              <TourIcon>
                <Flight />
              </TourIcon>
              <TourDescription>
                <h3>
                  <HotelName hotelId={order.hotelId} name={order.hotelName} />
                </h3>
                <p>{order.room}</p>
              </TourDescription>
              <TourPrice>
                <CurrencyFormat value={order.amount} currencyCode="RUB" />
                <span>стоимость тура</span>
              </TourPrice>
            </TourHero>
            <TripFacts>
              <Fact>
                <Calendar />
                <span>Дата поездки</span>
                <p>{order.dates}</p>
              </Fact>
              <Fact>
                <Clock />
                <span>Продолжительность</span>
                <p>{order.nights} ночей</p>
              </Fact>
              <Fact>
                <TouristIcon />
                <span>Туристы</span>
                <p>{order.touristsCount}</p>
              </Fact>
            </TripFacts>
          </Block>

          <TouristsBlock orderId={order.id} tourists={order.tourists} />
          <OrderHistory events={order.events} />
        </MainColumn>

        <SideColumn>
          <PaymentBlock>
            <PaymentHead>
              <h3>Оплата</h3>
              <PaymentStatus $paid={order.paymentStatus !== 'unpaid'}>
                {order.paymentStatus === 'unpaid'
                  ? 'Оплата ожидается'
                  : 'Оплачено полностью'}
              </PaymentStatus>
            </PaymentHead>
            <PaymentAmount>
              <span>Оплачено</span>
              <strong>
                <CurrencyFormat value={order.paidAmount} currencyCode="RUB" />
              </strong>
            </PaymentAmount>
            <PaymentTotal>
              из <CurrencyFormat value={order.amount} currencyCode="RUB" />
            </PaymentTotal>
            {order.paymentStatus === 'unpaid' && (
              <PaymentLinkActions>
                <CopyOnClick
                  valueForCopy={paymentUrl}
                  hintText="Скопировать ссылку на оплату"
                >
                  <PaymentLinkButton size="flex">
                    Скопировать ссылку на оплату
                  </PaymentLinkButton>
                </CopyOnClick>
              </PaymentLinkActions>
            )}
          </PaymentBlock>

          <Block>
            <BlockTitle>Связанные сущности</BlockTitle>
            <ClientRelation
              client={{
                id: order.clientId,
                name: order.clientName,
                surname: order.clientSurname,
                fullName: order.touristName,
              }}
            />
          </Block>
        </SideColumn>
      </DetailGrid>
    </Details>
  );
};

const Details = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
`;

const DetailsHeader = styled.div`
  border-bottom: 1px solid #e1e3e6;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  padding: 15px 18px;

  h2 {
    font-size: 19px;
    margin: 4px 0;
  }
`;

const Muted = styled.div`
  color: #919499;
  font-size: 12px;
  line-height: 17px;
`;

const ClientLink = styled.div`
  color: #007aff;
  font-size: 12px;
`;

const HeaderStatus = styled(OrderStatus)`
  margin: 0;
`;

const DetailGrid = styled.div`
  background: #f0f1f2;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(430px, 1.3fr) minmax(285px, 0.7fr);
  min-height: 0;
  overflow: auto;
  padding: 12px;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Block = styled.section`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const BlockTitle = styled.h3`
  border-bottom: 1px solid #eceef0;
  font-size: 15px;
  margin: 0;
  padding: 13px 14px;
`;

const TourHero = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 76px minmax(0, 1fr) auto;
  padding: 14px;
`;

const TourIcon = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #d7ebff, #eff7ff);
  border-radius: 6px;
  color: #007aff;
  display: flex;
  justify-content: center;

  svg {
    height: 31px;
    width: 31px;
  }
`;

const TourDescription = styled.section`
  align-content: center;
  display: grid;
  gap: 4px;
  min-width: 0;

  h3,
  p {
    margin: 0;
  }

  h3 {
    font-size: 15px;
    font-weight: 400;
  }

  p,
  small {
    color: #919499;
  }
`;

const TourPrice = styled.div`
  align-content: center;
  display: grid;
  font-size: 20px;
  font-weight: 700;
  justify-items: end;

  span {
    color: #919499;
    font-size: 11px;
    font-weight: 400;
  }
`;

const TripFacts = styled.div`
  border-top: 1px solid #eceef0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
`;

const Fact = styled.div`
  display: grid;
  gap: 2px 7px;
  grid-template-columns: 20px 1fr;
  padding: 11px 14px;

  & + & {
    border-left: 1px solid #eceef0;
  }

  svg {
    color: #007aff;
    grid-row: 1 / 3;
    height: 17px;
    width: 17px;
  }
  svg path,
  svg circle {
    stroke: currentColor;
    stroke-width: 1px;
    vector-effect: non-scaling-stroke;
  }

  span {
    color: #919499;
    font-size: 11px;
  }

  p {
    font-size: 12px;
  }
`;

// В круге Avatar много линий (лицо, волосы, плечи) — с той же обводкой он кажется жирнее часов и календаря.
const TouristIcon = styled(Avatar)`
  && path,
  && circle {
    stroke-width: 0.7px;
  }
`;

const PaymentBlock = styled(Block)`
  padding-bottom: 14px;
`;

const PaymentHead = styled.div`
  align-items: center;
  border-bottom: 1px solid #eceef0;
  display: flex;
  justify-content: space-between;
  padding: 13px 14px;

  h3 {
    font-size: 15px;
    margin: 0;
  }
`;

const PaymentStatus = styled.span<{ $paid: boolean }>`
  background: ${({ $paid }) => ($paid ? '#e8f8f0' : '#fff1e5')};
  border-radius: 11px;
  color: ${({ $paid }) => ($paid ? '#16814a' : '#bd5c13')};
  font-size: 11px;
  font-weight: 700;
  padding: 4px 7px;
`;

const PaymentAmount = styled.div`
  align-items: baseline;
  display: flex;
  justify-content: space-between;
  padding: 15px 14px 10px;

  > span {
    color: #919499;
    font-size: 12px;
  }

  strong {
    font-size: 21px;
  }
`;

const PaymentTotal = styled.div`
  color: #919499;
  font-size: 12px;
  padding: 0 14px;
  text-align: right;
`;

const PaymentLinkActions = styled.div`
  padding: 14px 14px 0;
`;

const PaymentLinkButton = styled(Button)`
  text-transform: none;
  width: 100%;
`;

export default OrderDetails;
